import * as Sentry from '@sentry/react'
import { logger } from '@upadelmatch/core'
import { env as firebaseEnv } from '@/core/firebase/config'

/** Injected at build time from package.json (vite.config define). */
export const APP_VERSION = import.meta.env.VITE_APP_VERSION as string

/**
 * Sentry `environment` — resolved from the Firebase env so the tag matches
 * the backend project the player is actually hitting (same rule as
 * clubmanager's core/sentry.ts).
 */
export const SENTRY_ENV = import.meta.env.VITE_SENTRY_ENV ?? firebaseEnv

/**
 * Initialise Sentry when a DSN is provided via build-time env. The DSN is
 * public by design (rate-limited + origin-gated on the Sentry dashboard);
 * with no DSN every capture call is a silent no-op, so this ships wired and
 * turns on the moment `VITE_SENTRY_DSN` lands in the env files.
 *
 * The shared @upadelmatch/core logger already routes warn/error to Sentry
 * (PII-sanitized) — init here is all that's missing for the player app.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    if (import.meta.env.PROD) {
      logger.w('Sentry: VITE_SENTRY_DSN is not set — player errors will not be reported.')
    } else {
      logger.i(`Sentry disabled (no DSN) · env=${SENTRY_ENV} version=${APP_VERSION}`)
    }
    return
  }

  Sentry.init({
    dsn,
    environment: SENTRY_ENV,
    release: `usersmatch-pwa@${APP_VERSION}`,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Drop events from local `npm run dev` sessions — HMR ghosts, watcher
      // races, port-bouncing service-worker registrations. They are transient
      // by definition, unactionable from a production stack, and they swamp
      // the unresolved queue so real signal cannot be seen.
      //
      // Measured 2026-08-09: USERMATCH-PWA-2 alone was **185 of the 196 events
      // in the queue (94%)** — "Failed to register a ServiceWorker for scope
      // ('http://localhost:5180/')", every one of them from our own dev
      // servers and persona walks. clubmanager has had this filter since it
      // closed 25 issues at a stroke; this app got the ignoreErrors list and
      // not the hostname guard.
      //
      // Only LOOPBACK hosts are dropped. The deployed dev site
      // (upadelmatch-usersmatch-dev.web.app) still reports everything — that
      // is where pre-cut QA runs, and its events are the ones worth reading.
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
          return null
        }
      }
      return event
    },
    ignoreErrors: IGNORED_ERRORS,
  })
  Sentry.setTag('app', 'usersmatch-pwa')
  Sentry.setTag('firebaseEnv', firebaseEnv)
  logger.i(`Sentry enabled · env=${SENTRY_ENV} release=${APP_VERSION}`)
}

/**
 * EXPORTED so a test can assert against the REAL filter.
 *
 * It used to be an inline literal, and one of its entries was dead for its
 * whole life: `/^MembershipError/` never matched, because `MembershipError`
 * subclassed `Error` without setting `name`, so Sentry titled every membership
 * block `Error: needs-gender`. Nothing could notice — a filter that matches
 * nothing and a filter that matches everything it should look identical from
 * inside this file.
 *
 * `matches-repository.test.ts` now runs the real reasons through this list, so
 * renaming the class or editing the regex fails a test instead of quietly
 * re-opening the leak.
 */
export const IGNORED_ERRORS: (string | RegExp)[] = [
  // Expected user errors, surfaced inline on the auth forms.
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/email-already-in-use',
  'auth/network-request-failed',
  'auth/popup-closed-by-user',
  // Membership blocks are reason-coded toasts, not bugs (match full,
  // wrong category…) — the MembershipError name carries the reason.
  /^MembershipError/,
  // ⚠️ 'Network request failed' / 'Failed to fetch' USED TO BE FILTERED HERE
  // and must not come back. In this app the fetches that matter ARE the
  // Firestore writes — join, cancel, record-result. Worse, `logger.e`
  // JSON-stringifies its context INTO the event message, so a filter on
  // that substring silently ate the very mutation failures we added
  // reporting for (both the captureMessage and captureException paths
  // match `ignoreErrors`). A dropped join is exactly the bug we need to see.
  // Transient browser/IDB noise that genuinely self-heals stays filtered.
  'ResizeObserver loop limit exceeded',
  'is not a valid JavaScript MIME type',
  'Connection to Indexed Database server lost',
  'UnknownError: Connection to Indexed Database server lost',
]
